/**
 * stock-homologation - Catálogo único das regras de Homologação de rastreadores.
 *
 * Espelha `_stock_homologation_target` (servidor), que continua sendo a validação
 * definitiva. Aqui serve apenas para montar opções e mensagens na tela.
 *
 * RELEASE: 2026-10-11 v1.1.0 - Rastreador avulso (sem kit ativo) aceita qualquer resultado.
 * RELEASE: 2026-10-11 v1.0.0 - Versão inicial (tipos Alterar Status e Configuração).
 */

export type HomologationType = 'status_change' | 'configuration';
export type HomologationResult = 'approved' | 'rejected_damaged' | 'rejected_maintenance' | 'lost' | 'indemnified';

export const HOMOLOGATION_TYPES: { value: HomologationType; label: string; description: string }[] = [
  { value: 'status_change', label: 'Alterar Status do Rastreador', description: 'Altera a situação operacional do rastreador.' },
  { value: 'configuration', label: 'Configuração', description: 'Registra testes de configuração, comandos e firmware, sem alterar a situação.' },
];

interface Rule { result: HomologationResult; label: string; allowed: string[] | null; target: string | null }

/** Regras por tipo. `allowed = null` aceita qualquer situação; `target = null` não altera. */
export const HOMOLOGATION_RULES: Record<HomologationType, Rule[]> = {
  status_change: [
    { result: 'approved', label: 'Aprovado', allowed: ['homologation', 'maintenance', 'unavailable'], target: 'active' },
    { result: 'rejected_damaged', label: 'Reprovado – Estragado', allowed: ['homologation', 'maintenance', 'damaged'], target: 'damaged' },
    { result: 'rejected_maintenance', label: 'Reprovado – Manutenção', allowed: ['homologation', 'maintenance', 'damaged'], target: 'maintenance' },
    { result: 'lost', label: 'Extraviado', allowed: ['homologation'], target: 'lost' },
    { result: 'indemnified', label: 'Indenizado', allowed: ['homologation'], target: 'indemnified' },
  ],
  // Extraviado e Indenizado não se aplicam a Configuração (ficam ocultos)
  configuration: [
    { result: 'approved', label: 'Aprovado', allowed: null, target: null },
    { result: 'rejected_damaged', label: 'Reprovado – Estragado', allowed: null, target: null },
    { result: 'rejected_maintenance', label: 'Reprovado – Manutenção', allowed: null, target: null },
  ],
};

/** Rótulo do tipo/resultado para listagens. */
export const homologationTypeLabel = (t: string) => HOMOLOGATION_TYPES.find((x) => x.value === t)?.label ?? t;
export const homologationResultLabel = (r: string) =>
  HOMOLOGATION_RULES.status_change.find((x) => x.result === r)?.label ?? r;

/** Resultado reprovado exige observação. */
export const homologationRequiresNotes = (r: HomologationResult) => r.startsWith('rejected_');

/**
 * Avalia uma combinação: devolve a nova situação ou o motivo do bloqueio.
 * Rastreador Instalado nunca pode ser homologado.
 */
export function evaluateHomologation(
  type: HomologationType,
  result: HomologationResult,
  currentStatus: string,
  inActiveKit = true,
): { ok: true; target: string } | { ok: false; reason: string } {
  if (currentStatus === 'installed') {
    return { ok: false, reason: 'Rastreador instalado em veículo. Retorne-o ao estoque antes.' };
  }
  const rule = HOMOLOGATION_RULES[type].find((r) => r.result === result);
  if (!rule) return { ok: false, reason: 'Resultado não aplicável a este tipo.' };
  // Avulso: libera qualquer resultado, exceto manter a mesma situação
  if (type === 'status_change' && !inActiveKit) {
    if (rule.target === currentStatus) return { ok: false, reason: 'O rastreador já está nesta situação.' };
    return { ok: true, target: rule.target ?? currentStatus };
  }
  if (rule.allowed && !rule.allowed.includes(currentStatus)) {
    return { ok: false, reason: 'A situação atual do rastreador não permite este resultado.' };
  }
  return { ok: true, target: rule.target ?? currentStatus };
}

/**
 * BR-EST-045: resultado Aprovado exige a plataforma homologada (espelha `create_stock_homologation`).
 */
export function homologationRequiresPlatform(result: HomologationResult | null): boolean {
  return result === 'approved';
}
